import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IPlace } from 'app/shared/model/place.model';
import { getEntities as getPlaces } from 'app/entities/place/place.reducer';
import { ITour } from 'app/shared/model/tour.model';
import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { ITourExtra } from 'app/shared/model/tour-extra.model';
import { getEntities as getTourExtras } from 'app/entities/tour-extra/tour-extra.reducer';
import { IWebPage } from 'app/shared/model/web-page.model';
import { getEntities as getWebPages } from 'app/entities/web-page/web-page.reducer';
import { ITag } from 'app/shared/model/tag.model';
import { getEntity, updateEntity, createEntity, reset } from './tag.reducer';

export const TagUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const places = useAppSelector(state => state.place.entities);
  const tours = useAppSelector(state => state.tour.entities);
  const tourExtras = useAppSelector(state => state.tourExtra.entities);
  const webPages = useAppSelector(state => state.webPage.entities);
  const tagEntity = useAppSelector(state => state.tag.entity);
  const loading = useAppSelector(state => state.tag.loading);
  const updating = useAppSelector(state => state.tag.updating);
  const updateSuccess = useAppSelector(state => state.tag.updateSuccess);

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/tag' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({});
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getPlaces({}));
    dispatch(getTours({}));
    dispatch(getTourExtras({}));
    dispatch(getWebPages({}));
  }, []);

  useEffect(() => {
    if (tagEntity) {
      setFormData({
        ...tagEntity,
        places: tagEntity?.places?.map(e => e.id.toString()),
        tours: tagEntity?.tours?.map(e => e.id.toString()),
        tourExtras: tagEntity?.tourExtras?.map(e => e.id.toString()),
        webPages: tagEntity?.webPages?.map(e => e.id.toString()),
      });
    }
  }, [tagEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleMultiSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const renderMultiSelectValue = (selected: any[]) => {
    return selected?.join(', ') || '';
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };

    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    const entity = {
      ...tagEntity,
      ...values,
      places: values.places ? mapIdList(values.places) : [],
      tours: values.tours ? mapIdList(values.tours) : [],
      tourExtras: values.tourExtras ? mapIdList(values.tourExtras) : [],
      webPages: values.webPages ? mapIdList(values.webPages) : [],
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.tag.home.createOrEditLabel">Create or edit a Tag</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            {!isNew ? (
              <TextField id="tag-id" name="id" label={translate('global.field.id')} value={formData.id || ''} fullWidth InputProps={{ readOnly: true }} />
            ) : null}
            <TextField
              label={translate('jhipsterSampleApplicationApp.tag.code')}
              id="tag-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tag.icon')}
              id="tag-icon"
              name="icon"
              data-cy="icon"
              value={formData.icon || ''}
              onChange={handleChange('icon')}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="tag-places-label">{translate('jhipsterSampleApplicationApp.tag.place')}</InputLabel>
              <Select
                labelId="tag-places-label"
                id="tag-places"
                data-cy="places"
                multiple
                value={formData.places || []}
                label={translate('jhipsterSampleApplicationApp.tag.place')}
                onChange={handleMultiSelectChange('places')}
                renderValue={renderMultiSelectValue}
              >
                {places
                  ? places.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="tag-tours-label">{translate('jhipsterSampleApplicationApp.tag.tour')}</InputLabel>
              <Select
                labelId="tag-tours-label"
                id="tag-tours"
                data-cy="tours"
                multiple
                value={formData.tours || []}
                label={translate('jhipsterSampleApplicationApp.tag.tour')}
                onChange={handleMultiSelectChange('tours')}
                renderValue={renderMultiSelectValue}
              >
                {tours
                  ? tours.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="tag-tourExtras-label">{translate('jhipsterSampleApplicationApp.tag.tourExtra')}</InputLabel>
              <Select
                labelId="tag-tourExtras-label"
                id="tag-tourExtras"
                data-cy="tourExtras"
                multiple
                value={formData.tourExtras || []}
                label={translate('jhipsterSampleApplicationApp.tag.tourExtra')}
                onChange={handleMultiSelectChange('tourExtras')}
                renderValue={renderMultiSelectValue}
              >
                {tourExtras
                  ? tourExtras.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="tag-webPages-label">{translate('jhipsterSampleApplicationApp.tag.webPage')}</InputLabel>
              <Select
                labelId="tag-webPages-label"
                id="tag-webPages"
                data-cy="webPages"
                multiple
                value={formData.webPages || []}
                label={translate('jhipsterSampleApplicationApp.tag.webPage')}
                onChange={handleMultiSelectChange('webPages')}
                renderValue={renderMultiSelectValue}
              >
                {webPages
                  ? webPages.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/tag"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TagUpdate;
